const CHART_SETTINGS_KEY = 'arz:coin-chart-settings';
const STABLE_COMMENTS_KEY = 'arz:coin-chart-comments:stable';
const MOBILE_LIVE_PRICE_KEY = 'arz:coin-chart-live-price:mobile';
const DEFAULT_CHART_SETTINGS = Object.freeze({
  livePrice: true,
  comments: true,
  logarithmic: false,
});

function getChartSettingsStorage(browser) {
  try {
    return browser?.localStorage;
  } catch (_error) {
    return undefined;
  }
}

function coinCurrencyKey(slug) {
  return `arz:coin-chart-currency:${encodeURIComponent(String(slug || ''))}`;
}

function loadCoinChartCurrency(storage, slug, isStableCoin) {
  try {
    const stored = storage?.getItem(coinCurrencyKey(slug));
    if (stored === 'usd' || stored === 'irt') return stored;
  } catch (_error) {
    // Use the existing chart default when storage is unavailable.
  }
  return isStableCoin ? 'irt' : 'usd';
}

function saveCoinChartCurrency(storage, slug, currency) {
  const normalized = currency === 'irt' ? 'irt' : 'usd';
  try {
    storage?.setItem(coinCurrencyKey(slug), normalized);
  } catch (_error) {
    // The active chart still uses the returned value.
  }
  return normalized;
}

function getChartCurrencyVisibility(currency) {
  return currency === 'irt'
    ? { series: [false, true, false, false, true, true], axes: [true, false] }
    : { series: [true, false, true, true, false, false], axes: [false, true] };
}

function resolveChartCurrencyForRender(currentCurrency, _isFirstTime, _isStableCoin, _tomanIsVisible) {
  return currentCurrency === 'irt' ? 'irt' : 'usd';
}

function shouldRenderLivePricePlotLines(enabled, prefix, _isMobile) {
  return Boolean(enabled && prefix === 'coindetails');
}

function isChartSettingsReady(hasChartContainer, rangeButtonCount) {
  return Boolean(hasChartContainer && rangeButtonCount > 0);
}

// Mobile range buttons are centred below the chart. Trailing icon controls (settings,
// screenshot) take extra slots at the same pitch as the buttons, so the whole row is evenly
// distributed and centred; spacing tightens from 15px (minimum 4px) only on narrow charts.
const MOBILE_RANGE_BUTTON_COUNT = 6;
const MOBILE_RANGE_BUTTON_WIDTH = 32;
const MOBILE_RANGE_SPACING = 15;
const MOBILE_RANGE_MIN_SPACING = 4;
const MOBILE_RANGE_EDGE = 6;
const MOBILE_RANGE_ICON_WIDTH = 26;
// Highstock 8 centres the mobile button row ~2px left of the chart centre at every width.
const MOBILE_RANGE_CENTER_BIAS = 2;

function getMobileRangeSelectorLayout(containerWidth, trailingSlots) {
  const slots = Math.max(0, Math.floor(Number(trailingSlots) || 0));
  if (!slots) return { buttonSpacing: MOBILE_RANGE_SPACING, x: 0 };
  const items = MOBILE_RANGE_BUTTON_COUNT + slots;
  const available = (Number(containerWidth) || 0) - 2 * MOBILE_RANGE_EDGE - items * MOBILE_RANGE_BUTTON_WIDTH;
  const buttonSpacing = Math.max(MOBILE_RANGE_MIN_SPACING, Math.min(MOBILE_RANGE_SPACING, Math.floor(available / (items - 1))));
  // x: visual shift of the button row so the visible row (buttons through the last, narrower
  // icon) is centred: half the trailing slots, less half the icon's inset within its slot.
  const pitch = MOBILE_RANGE_BUTTON_WIDTH + buttonSpacing;
  const inset = (MOBILE_RANGE_BUTTON_WIDTH - MOBILE_RANGE_ICON_WIDTH) / 2;
  return { buttonSpacing, x: -(slots * pitch - inset) / 2 + MOBILE_RANGE_CENTER_BIAS };
}

// Centres for `count` slots after the last range button, continuing the buttons' pitch.
function getRangeRowTrailingCenters(buttonRects, count) {
  const centers = Array.from(buttonRects || [], (rect) => (rect.left + rect.right) / 2).sort((a, b) => a - b);
  if (centers.length < 2) return [];
  const last = centers[centers.length - 1];
  const pitch = (last - centers[0]) / (centers.length - 1);
  return Array.from({ length: count }, (_, index) => last + pitch * (index + 1));
}

function isMobileChartRequest(search) {
  return new URLSearchParams(String(search || '')).get('mobile') === '1';
}

async function startChartBeforeAuxiliary(startChart, loadAuxiliary) {
  const auxiliaryPromise = loadAuxiliary();
  startChart();
  return auxiliaryPromise;
}

function normalizeCoinHistoryPoints(points) {
  if (!Array.isArray(points)) return [];
  return points.map((point) => Array.isArray(point) ? [...point] : point);
}

function syncLivePricePlotLines(chart, enabled, plotLines = []) {
  if (!chart?.yAxis) return false;
  for (const axis of chart.yAxis.slice(0, 2)) axis?.removePlotLine?.('lastPrice');
  if (enabled) {
    chart.yAxis[0]?.addPlotLine?.(plotLines[0]);
    chart.yAxis[1]?.addPlotLine?.(plotLines[1]);
  }
  chart.redraw?.();
  return true;
}

// Hidden/cropped series can lose rendered points after a chart resize.
// Address the raw tail by index, never a grouped or visible points array.
function updateLiveChartTail(series, point) {
  const data = series?.options?.data;
  if (!Array.isArray(data) || !data.length) return false;
  const index = data.length - 1;
  const rendered = series.data?.[index];
  if (rendered?.update) rendered.update(point, false);
  else {
    const next = data.slice();
    next[index] = point;
    series.setData(next, false, false, false);
  }
  return true;
}

function loadChartSettings(storage, isMobile = false, isStable = false) {
  try {
    const stored = JSON.parse(storage?.getItem(CHART_SETTINGS_KEY) || '{}');
    const storedMobileLivePrice = storage?.getItem(MOBILE_LIVE_PRICE_KEY);
    return {
      livePrice: isMobile
        ? storedMobileLivePrice == null ? false : storedMobileLivePrice === 'true'
        : typeof stored.livePrice === 'boolean' ? stored.livePrice : true,
      comments: isStable ? storage?.getItem(STABLE_COMMENTS_KEY) === 'true'
        : typeof stored.comments === 'boolean' ? stored.comments : true,
      logarithmic: typeof stored.logarithmic === 'boolean' ? stored.logarithmic : false,
    };
  } catch (_error) {
    return { ...DEFAULT_CHART_SETTINGS, livePrice: isMobile ? false : true, comments: !isStable };
  }
}

function saveChartSettings(storage, settings, isMobile = false, isStable = false) {
  const normalized = {
    livePrice: typeof settings?.livePrice === 'boolean' ? settings.livePrice : true,
    comments: typeof settings?.comments === 'boolean' ? settings.comments : true,
    logarithmic: typeof settings?.logarithmic === 'boolean' ? settings.logarithmic : false,
  };
  try {
    // Stable assets have their own opt-in; unrelated settings must not change
    // the existing comment preference for other coins.
    const sharedComments = isStable ? loadChartSettings(storage).comments : normalized.comments;
    if (isStable) storage?.setItem(STABLE_COMMENTS_KEY, String(normalized.comments));
    if (isMobile) {
      storage?.setItem(MOBILE_LIVE_PRICE_KEY, String(normalized.livePrice));
      const shared = loadChartSettings(storage, false);
      storage?.setItem(CHART_SETTINGS_KEY, JSON.stringify({
        ...shared,
        comments: sharedComments,
        logarithmic: normalized.logarithmic,
      }));
    } else storage?.setItem(CHART_SETTINGS_KEY, JSON.stringify({ ...normalized, comments: sharedComments }));
  } catch (_error) {
    // Storage may be unavailable; the active page can still use the returned state.
  }
  return normalized;
}

function updateChartSetting(storage, name, enabled) {
  const settings = loadChartSettings(storage);
  if (name === 'livePrice' || name === 'comments' || name === 'logarithmic') settings[name] = Boolean(enabled);
  return saveChartSettings(storage, settings);
}

function createChartSettingsStore(storage, options = {}) {
  const isMobile = Boolean(options.isMobile);
  let isStable = false;
  let settings;
  const initialize = () => {
    if (settings) return;
    // Bundles may execute in the head, before the inline coin_info exists.
    isStable = (typeof options.isStable === 'function' ? options.isStable() : options.isStable) === true;
    settings = loadChartSettings(storage, isMobile, isStable);
  };
  const subscribers = new Set();

  return {
    get() {
      initialize();
      return { ...settings };
    },
    set(name, enabled) {
      initialize();
      const nextSettings = { ...settings };
      if (name === 'livePrice' || name === 'comments' || name === 'logarithmic') nextSettings[name] = Boolean(enabled);
      settings = saveChartSettings(storage, nextSettings, isMobile, isStable);
      for (const subscriber of subscribers) subscriber({ ...settings });
      return { ...settings };
    },
    subscribe(subscriber) {
      initialize();
      subscribers.add(subscriber);
      subscriber({ ...settings });
      return () => subscribers.delete(subscriber);
    },
  };
}

module.exports = {
  CHART_SETTINGS_KEY,
  DEFAULT_CHART_SETTINGS,
  createChartSettingsStore,
  getChartCurrencyVisibility,
  getMobileRangeSelectorLayout,
  getRangeRowTrailingCenters,
  isChartSettingsReady,
  isMobileChartRequest,
  startChartBeforeAuxiliary,
  normalizeCoinHistoryPoints,
  resolveChartCurrencyForRender,
  shouldRenderLivePricePlotLines,
  getChartSettingsStorage,
  loadCoinChartCurrency,
  saveCoinChartCurrency,
  syncLivePricePlotLines,
  updateLiveChartTail,
  loadChartSettings,
  saveChartSettings,
  updateChartSetting,
};
